import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import qs from 'qs';
import { useServiceToken } from '@/auth/JwtAuthenticator/JwtAuthenticator';
import { useAppConfigContext } from '@/context/AppConfig';
import { request } from '@/utils/index';
import { Notification } from './types';

const queryKey = 'notifications';

class Sriracha {
  public static async getNotifications(token: string) {
    const response = await request('/sriracha/api/notifications', {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.notifications) return [];
    return Sriracha.orderNotifications(response.notifications);
  }

  public static async deleteNotification(token: string, id: number) {
    return await request(`/sriracha/api/notifications/${id}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async readNotification(token: string, id: number) {
    return await request(`/sriracha/api/notifications/${id}?mark_read=true`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async unreadNotification(token: string, id: number) {
    return await request(`/sriracha/api/notifications/${id}?mark_unread=true`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async readAllNotifications(token: string) {
    return await request('/sriracha/api/notifications/mark_all_as_read', {
      method: 'PATCH',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async createNotification(
    token: string,
    customQueryParams: unknown
  ) {
    const queryString = qs.stringify(customQueryParams);
    return request(`/sriracha/api/notifications?${queryString}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async getSuggestedReportNotificationSources(token: string) {
    return await request(
      '/sriracha/api/notifications/suggested_report_sources',
      {
        method: 'GET',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  public static orderNotifications(notifications: Notification[]) {
    return notifications.sort(
      (a, b) =>
        new Date().getTime() -
        new Date(a.created_at).getTime() -
        (new Date().getTime() - new Date(b.created_at).getTime())
    );
  }
}

export function useSriracha() {
  const queryClient = useQueryClient();
  const { logError } = useAppConfigContext();
  const { data: tokenData } = useServiceToken('sriracha');

  const notifications = useQuery({
    queryKey: [queryKey],
    queryFn: () => Sriracha.getNotifications(tokenData?.token),

    onError: (error) => {
      logError('Error fetching notifications', error as Error);
    },
    enabled: !!tokenData?.token,
    staleTime: 5 * 60 * 1000,
    cacheTime: 6 * 60 * 1000,
  });

  const deleteNotification = useMutation({
    mutationFn: (notificationId: number) => {
      return Sriracha.deleteNotification(tokenData?.token, notificationId);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
    },
    onError: (error) => {
      logError('Delete notification error', error as Error);
    },
  });

  const readNotification = useMutation({
    mutationFn: (notificationId: number) => {
      return Sriracha.readNotification(tokenData?.token, notificationId);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
    },
    onError: (error) => {
      logError('Reading notification error', error as Error);
    },
  });

  const unreadNotification = useMutation({
    mutationFn: (notificationId: number) => {
      return Sriracha.unreadNotification(tokenData?.token, notificationId);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
    },
    onError: (error) => {
      logError('Unreading notification error', error as Error);
    },
  });

  const readAllNotifications = useMutation({
    mutationFn: () => {
      return Sriracha.readAllNotifications(tokenData?.token);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
    },
    onError: (error) => {
      logError('Reading all notifications error', error as Error);
    },
  });

  const sendNotification = useMutation({
    mutationFn: (customQueryParams: unknown) => {
      return Sriracha.createNotification(tokenData?.token, customQueryParams);
    },

    onSuccess: () => {
      // setTimeout is used to allow the notification to be created and queued up before the query is invalidated #TemporaryFix
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['suggestedReportSources'] });
        queryClient.invalidateQueries({
          queryKey: [queryKey],
        });
      }, 2500);
    },
    onError: (error) => {
      logError('Create notification error', error as Error);
    },
  });

  const getSuggestedReportNotificationSources = useQuery({
    queryKey: ['suggestedReportSources'],
    queryFn: () =>
      Sriracha.getSuggestedReportNotificationSources(tokenData?.token),

    onError: (error) => {
      logError('Error fetching notifications', error as Error);
    },
    enabled: !!tokenData?.token,
    staleTime: 5 * 60 * 1000,
    cacheTime: 6 * 60 * 1000,
  });

  return {
    ...notifications,
    readNotification,
    unreadNotification,
    readAllNotifications,
    sendNotification,
    getSuggestedReportNotificationSources,
    deleteNotification,
  };
}
